/**
 * Pure logic for the System Card release map (`ReleaseMap.tsx`, LEV-2912):
 * lane order and colors, filters and their URL form, counts, the date scale
 * and date formatting. No React, no DOM, so it unit-tests in node.
 *
 * Every date here is a UTC calendar date. Release dates are ISO `YYYY-MM-DD`
 * strings with no time zone, and the page is statically prerendered, so a
 * local-time parse would shift a dot by a day for some viewers and make the
 * server and client markup disagree.
 */
import { RELEASE_LANES, type ModelRelease, type ReleaseLane } from "@/constants/systemCard.types";

export type WeightsFilter = "all" | "open" | "closed";
export type RangeFilter = "12m" | "6m" | "all";
/** "all", or one `ModelRelease.mod` value present in the data. */
export type TypeFilter = string;

export interface ReleaseMapFilters {
  weights: WeightsFilter;
  type: TypeFilter;
  range: RangeFilter;
}

export const DEFAULT_RELEASE_MAP_FILTERS: ReleaseMapFilters = { weights: "all", type: "all", range: "all" };

export const WEIGHTS_OPTIONS: { value: WeightsFilter; label: string }[] = [
  { value: "all", label: "Both" },
  { value: "closed", label: "Closed" },
  { value: "open", label: "Open" },
];

export const RANGE_OPTIONS: { value: RangeFilter; label: string }[] = [
  { value: "12m", label: "12 months" },
  { value: "6m", label: "6 months" },
  { value: "all", label: "All" },
];

const RANGE_MONTHS: Record<Exclude<RangeFilter, "all">, number> = { "12m": 12, "6m": 6 };

/** Display order and labels for known modalities; unknown ones sort after, alphabetically. */
const TYPE_ORDER = ["text", "agent", "coding", "image", "video", "voice"];
const TYPE_LABELS: Record<string, string> = {
  text: "Text",
  agent: "Agents",
  coding: "Coding",
  image: "Image",
  video: "Video",
  voice: "Voice",
  science: "Science",
};

export function typeLabel(mod: string): string {
  return TYPE_LABELS[mod] ?? mod.charAt(0).toUpperCase() + mod.slice(1);
}

/** The type filter's options: only modalities the data actually holds, so no chip filters to nothing. */
export function typeOptions(releases: ModelRelease[]): string[] {
  const present = new Set(releases.map((r) => r.mod));
  const known = TYPE_ORDER.filter((m) => present.has(m));
  const rest = [...present].filter((m) => !TYPE_ORDER.includes(m)).sort();
  return [...known, ...rest];
}

/**
 * The preview's lane palette. Learn pages are light-only (no `dark:` styles in
 * `LearnPageChrome`), so there are no dark variants. A `Record` keyed by
 * `ReleaseLane` makes a lane added to `RELEASE_LANES` a type error here until
 * it has a color.
 */
export const RELEASE_LANE_COLORS: Record<ReleaseLane, string> = {
  Anthropic: "#D2622A",
  OpenAI: "#0E8A5F",
  Google: "#2468D8",
  xAI: "#2B2F36",
  Meta: "#7B4FE0",
  DeepSeek: "#4A55D6",
  Alibaba: "#9A3FD0",
  Mistral: "#11928F",
  "Google (Gemma)": "#3E8FC4",
  "OpenAI (gpt-oss)": "#3C7D8A",
  "Moonshot (Kimi)": "#B04FBF",
  "Zhipu (GLM)": "#6D6AE8",
  MiniMax: "#8C5BA8",
  Xiaomi: "#0FA3B1",
  NVIDIA: "#2F9C77",
  "Other open": "#7E86B8",
};

/**
 * Lane rows top to bottom. `openBand` places the lane below the dividing line;
 * it describes the lab, not each release (Meta and Mistral also ship some
 * closed models, which still draw on their lab's lane as solid dots).
 */
const LANE_LAYOUT: { lane: ReleaseLane; openBand: boolean }[] = [
  { lane: "Anthropic", openBand: false },
  { lane: "OpenAI", openBand: false },
  { lane: "Google", openBand: false },
  { lane: "xAI", openBand: false },
  { lane: "Meta", openBand: true },
  { lane: "DeepSeek", openBand: true },
  { lane: "Alibaba", openBand: true },
  { lane: "Mistral", openBand: true },
  { lane: "Google (Gemma)", openBand: true },
  { lane: "OpenAI (gpt-oss)", openBand: true },
  { lane: "Moonshot (Kimi)", openBand: true },
  { lane: "Zhipu (GLM)", openBand: true },
  { lane: "MiniMax", openBand: true },
  { lane: "Xiaomi", openBand: true },
  { lane: "NVIDIA", openBand: true },
  { lane: "Other open", openBand: true },
];

/**
 * Lanes to draw, in layout order. With a weights filter, the matching band
 * plus any lane from the other band that still holds a matching release
 * (Meta and Mistral ship closed models too), so every counted release has a
 * dot. Pass the filtered rows for that; without them only the band is kept.
 */
export function orderedLanes(
  weights: WeightsFilter,
  rows: readonly Pick<ModelRelease, "lane">[] = [],
): { lane: ReleaseLane; openBand: boolean }[] {
  if (weights === "all") return LANE_LAYOUT;
  const withRows = new Set<string>(rows.map((r) => r.lane));
  return LANE_LAYOUT.filter((l) => l.openBand === (weights === "open") || withRows.has(l.lane));
}

/**
 * Horizontal nudge, in px, for a release that shares its lane and date with
 * an earlier one, so stacked dots stay separately clickable.
 */
export function collisionOffsets(rows: readonly Pick<ModelRelease, "lane" | "d" | "m">[], step = 10): Map<string, number> {
  const seen = new Map<string, number>();
  const out = new Map<string, number>();
  for (const r of rows) {
    const key = `${r.lane}|${r.d}`;
    const n = seen.get(key) ?? 0;
    seen.set(key, n + 1);
    out.set(`${r.lane}-${r.d}-${r.m}`, n * step);
  }
  return out;
}

const KNOWN_LANES = new Set<string>(RELEASE_LANES);
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export function parseIsoDate(iso: string): Date {
  return new Date(`${iso}T00:00:00Z`);
}

/**
 * Drops entries the map cannot place: an unknown lane (the preview once blanked
 * the whole page on one) or an unparseable date. Never throws; one bad row
 * costs one dot, not the map.
 */
export function dropUnknownLanes(
  releases: ModelRelease[],
  warn: (message: string) => void = (m) => console.warn(m),
): ModelRelease[] {
  return releases.filter((r) => {
    if (!KNOWN_LANES.has(r.lane)) {
      warn(`ReleaseMap: skipping "${r.m}", unknown lane "${String(r.lane)}"`);
      return false;
    }
    if (!ISO_DATE.test(r.d) || Number.isNaN(parseIsoDate(r.d).getTime())) {
      warn(`ReleaseMap: skipping "${r.m}", unparseable date "${r.d}"`);
      return false;
    }
    return true;
  });
}

function addMonthsUtc(date: Date, months: number): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + months, date.getUTCDate()));
}

/** Left edge of the x axis: N months before `updatedOn`, or the first of the earliest release's month. */
export function rangeStart(range: RangeFilter, updatedOn: string, releases: ModelRelease[]): Date {
  const end = parseIsoDate(updatedOn);
  if (range !== "all") return addMonthsUtc(end, -RANGE_MONTHS[range]);
  const earliest = releases.reduce<string | null>((min, r) => (min === null || r.d < min ? r.d : min), null);
  const first = earliest ? parseIsoDate(earliest) : addMonthsUtc(end, -12);
  return new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth(), 1));
}

export function filterReleases(
  releases: ModelRelease[],
  filters: ReleaseMapFilters,
  updatedOn: string,
): ModelRelease[] {
  const start = filters.range === "all" ? null : rangeStart(filters.range, updatedOn, releases);
  return releases.filter((r) => {
    if (start && parseIsoDate(r.d) < start) return false;
    if (filters.weights === "open" && !r.open) return false;
    if (filters.weights === "closed" && r.open) return false;
    if (filters.type !== "all" && r.mod !== filters.type) return false;
    return true;
  });
}

/** Released models only: an announced model with weights still pending is drawn but not counted. */
export function countReleases(releases: ModelRelease[]): number {
  return releases.filter((r) => !r.pending).length;
}

/** Newest first. Pending entries are included; the list marks them. */
/**
 * The vendor to show before a model name. Lane qualifiers such as
 * "Google (Gemma)" drop to the company; "Other open" returns null because
 * those model names already carry their maker (e.g. "Liquid AI LFM3 family").
 */
export function releaseVendor(lane: ReleaseLane): string | null {
  if (lane === "Other open") return null;
  return lane.replace(/\s*\(.*\)$/, "");
}

export function latestReleases(releases: ModelRelease[], n: number): ModelRelease[] {
  return [...releases].sort((a, b) => b.d.localeCompare(a.d)).slice(0, n);
}

export function makeDateScale(start: Date, end: Date, x0: number, x1: number): (iso: string | Date) => number {
  const span = end.getTime() - start.getTime() || 1;
  return (value) => {
    const t = (typeof value === "string" ? parseIsoDate(value) : value).getTime();
    return x0 + ((t - start.getTime()) / span) * (x1 - x0);
  };
}

/** The first of each month after `start`, up to and including `end`. */
export function monthTicks(start: Date, end: Date): Date[] {
  const ticks: Date[] = [];
  let d = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + 1, 1));
  while (d <= end) {
    ticks.push(d);
    d = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 1));
  }
  return ticks;
}

export function formatReleaseDate(iso: string): string {
  return parseIsoDate(iso).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

/** "Nov 1, 2026", for the narrow date column of the latest list. */
export function formatShortReleaseDate(iso: string): string {
  return parseIsoDate(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

export function formatMonthTick(date: Date, withYear: boolean): string {
  const month = date.toLocaleDateString("en-US", { month: "short", timeZone: "UTC" });
  return withYear ? `${month} ${String(date.getUTCFullYear()).slice(2)}` : month;
}

/** Short label drawn beside a generational dot. */
export function majorLabel(name: string): string {
  return name
    .replace(/\s*\(.*?\)\s*/g, " ")
    .replace(/ and .*/, " +")
    .trim();
}

function isWeights(v: string | null): v is WeightsFilter {
  return v === "all" || v === "open" || v === "closed";
}
function isRange(v: string | null): v is RangeFilter {
  return v === "12m" || v === "6m" || v === "all";
}

/** Reads `?weights=&type=&range=`; any value it does not recognise falls back to the default. */
export function parseReleaseMapFilters(params: URLSearchParams, types: string[]): ReleaseMapFilters {
  const weights = params.get("weights");
  const type = params.get("type");
  const range = params.get("range");
  return {
    weights: isWeights(weights) ? weights : DEFAULT_RELEASE_MAP_FILTERS.weights,
    type: type !== null && types.includes(type) ? type : DEFAULT_RELEASE_MAP_FILTERS.type,
    range: isRange(range) ? range : DEFAULT_RELEASE_MAP_FILTERS.range,
  };
}

/** Writes filters into `params` in place, omitting defaults so the plain URL stays plain. */
export function writeReleaseMapFilters(params: URLSearchParams, filters: ReleaseMapFilters): void {
  for (const key of ["weights", "type", "range"] as const) {
    if (filters[key] === DEFAULT_RELEASE_MAP_FILTERS[key]) params.delete(key);
    else params.set(key, filters[key]);
  }
}
